import React from 'react';
import Popper from '@mui/material/Popper';
import { styled } from '@mui/material/styles';
import Autocomplete, { autocompleteClasses } from '@mui/material/Autocomplete';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import {
  CascadeOption,
  getSelectedOptions,
} from '@/components/form/cascade-select/CascadeSelect';
import CascadeSelectOptions from '@/components/form/CascadeSelectOptions';
import Input from './Input';
import { useClintMediaQuery } from '@/utils/hooks/useClintMediaQuery';
import { Option } from '@/components/form/cascade-select/CascadeSelect';
import styles from '@/components/form/cascade-select/CascadeSelect.module.scss';
import { trackClickEvent } from '@/utils/site-track';

const SELECT_SELECTED = 'selected';
const SELECT_SHOW = 'show-option';

const DEFAULT_SELECT_OPTION = {
  parent: { ele: null, top: 0 },
  child: { ele: null, top: 0 },
};

interface SelectOption {
  parent: { ele: null | HTMLElement; top: number };
  child: { ele: null | HTMLElement; top: number };
}

interface CitySearchOptionsProps {
  options: CascadeOption[];
  onChange: (selectValue: Option, selectOptions: Option[]) => void;
  value?: Option;
  isHalfWidth?: boolean;
  className?: string;
  sx?: React.CSSProperties;
  label?: string;
  placeholder?: string;
  error?: boolean;
  helperText?: string;
  optionsClassName?: string;
  handleMobileSearch?: () => void;
  pageUniqueId?: string;
  labelColor?: string;
  dataEleId?: string;
  dataEleGroup?: string;
  dataObserverId?: string;
  autocompleteSx?: React.CSSProperties;
  popperSx?: React.CSSProperties;
  arrowIcon?: React.ReactNode;
  inputClassName?: string;
}

const CitySearchOptions = (props: CitySearchOptionsProps) => {
  const {
    value,
    onChange,
    options,
    isHalfWidth = false,
    className = '',
    label = '',
    labelColor = 'text-black',
    placeholder = '',
    sx,
    error,
    helperText,
    optionsClassName = '',
    handleMobileSearch,
    pageUniqueId = '',
    dataEleId,
    dataEleGroup,
    dataObserverId,
    autocompleteSx,
    popperSx,
    arrowIcon = <ArrowDropDownIcon sx={{ color: '#79747e' }} />,
    inputClassName,
  } = props;
  const isMobile = useClintMediaQuery();
  const isMobileSearch = isMobile && !!handleMobileSearch;

  const [inputFocus, setInputFocus] = React.useState(false);
  const [autoCompleteOpen, setAutoCompleteOpen] = React.useState(false);
  const [selectOption, setSelectOption] = React.useState<SelectOption>(
    DEFAULT_SELECT_OPTION,
  );
  const [searchValue, setSearchValue] = React.useState('');

  const selectMenuRef = React.useRef<HTMLDivElement>(null);
  const inputRef = React.useRef<HTMLInputElement | undefined>(undefined);

  const styledPopper = styled(Popper)({
    paddingTop: '4px',
    [`.${autocompleteClasses.listbox} .${autocompleteClasses.option}.${autocompleteClasses.focused}`]:
      {
        color: '#1861bf',
        background: 'rgba(24, 97, 191, 0.1)',
      },
    ...popperSx,
  });

  const cityOptions = React.useMemo(() => {
    if (Array.isArray(options) && options.length > 0) {
      const myOptions: Option[] = [];
      options.map((item) => {
        const id = item.id;
        const children = ('children' in item && item?.children) ?? [];
        if (id && id !== 'hot_cities' && children && children.length > 0) {
          children?.map(({ id, name, pinyin }) => {
            myOptions.push({
              id,
              name,
              pinyin,
              searchPinyin: pinyin?.replace(/[0-9]/g, ''),
            });
          });
        }
      });
      return myOptions;
    }
    return [];
  }, [options]);

  const search = React.useCallback(
    (value: string): Option[] => {
      const lowerCaseValue = value.replace(/[\x20-\x40]/g, '').toLowerCase();
      if (lowerCaseValue.length === 0) return [];
      const result: { id: string; name: string; pinyin: string }[] = [];
      const category = lowerCaseValue[0].match(/[a-z]/)
        ? 'searchPinyin'
        : 'name';
      for (const obj of cityOptions) {
        if (obj[category]?.startsWith(lowerCaseValue)) {
          result.push({ id: obj.id, name: obj.name, pinyin: obj.pinyin || '' });
          if (result.length >= 5) return result;
        }
      }
      return result;
    },
    [cityOptions],
  );

  const searchOptions = React.useMemo(
    () => search(searchValue),
    [searchValue, search],
  );

  const handleSelectOption = (value: SelectOption) => {
    const {
      parent: { ele: parentELe },
      child: { ele: currentEle },
    } = value;
    setSelectOption((prev) => {
      const {
        parent: { ele: prevParentEle },
        child: { ele: prevChildEle },
      } = prev;
      if (prevParentEle && prevParentEle !== parentELe) {
        prevParentEle.classList.remove(SELECT_SELECTED);
        prevParentEle.firstElementChild?.classList.remove(SELECT_SHOW);
      }
      if (prevChildEle && prevChildEle !== currentEle) {
        prevChildEle?.classList.remove(SELECT_SELECTED);
      }
      return value;
    });
  };

  const toggleDropdownMenu = React.useCallback((show: boolean): void => {
    setInputFocus(show);
    selectMenuRef.current?.classList.toggle(styles.show, show);
  }, []);

  const handleSelect = React.useCallback(
    (option: CascadeOption, event: any) => {
      const currentEle = event.target;
      if (!currentEle) return;

      const selectedOptions = getSelectedOptions(options, option.id);
      onChange(selectedOptions.slice(-1)[0], selectedOptions as Option[]);

      const childScrollELe = currentEle.parentNode;
      const parentELe = childScrollELe.parentNode.parentNode;
      const parentScrollEle = parentELe.parentNode;

      handleSelectOption({
        parent: { ele: parentELe, top: parentScrollEle.scrollTop },
        child: { ele: currentEle, top: childScrollELe.scrollTop },
      });

      setAutoCompleteOpen(false);
      toggleDropdownMenu(false);
      inputRef.current?.blur();
    },
    [options, onChange, toggleDropdownMenu],
  );

  React.useEffect(() => {
    if (!autoCompleteOpen) {
      toggleDropdownMenu(false);
      inputRef.current?.blur();
    }
  }, [autoCompleteOpen, toggleDropdownMenu]);

  React.useEffect(() => {
    const selectEle = selectMenuRef.current;
    const {
      parent: { ele: parentEle },
      child: { ele: childEle },
    } = selectOption;
    if (!selectEle || !parentEle || !childEle) {
      return;
    }
    const handleMove = (e: Event) => {
      if (!e.target || !parentEle.contains(e.target as Node)) {
        const childUlEle = childEle.parentNode?.parentNode as HTMLElement;
        childUlEle && childUlEle.classList.remove(SELECT_SHOW);
      }
    };
    selectEle.addEventListener('mouseover', handleMove);
    return () => {
      selectEle.removeEventListener('mousemove', handleMove);
    };
  }, [selectOption]);

  React.useEffect(() => {
    setAutoCompleteOpen(false);
    toggleDropdownMenu(false);
  }, [isMobile, toggleDropdownMenu]);

  return (
    <div className={`${className} relative`}>
      {label && (
        <label
          className={`block text-sm leading-5.5 font-semibold ${labelColor} mb-2 tablet:mb-3`}
        >
          {label}
        </label>
      )}
      <Autocomplete
        value={value}
        open={autoCompleteOpen}
        renderInput={(params) => (
          <Input
            placeholder={placeholder}
            autoComplete="off"
            inputRef={params.InputProps.ref}
            inputProps={params.inputProps}
            endAdornment={arrowIcon}
            endAdornmentClassName={`transition-transform duration-300 ${inputFocus ? 'rotate-180' : 'rotate-0'}`}
            myInputRef={inputRef}
            error={error}
            helperText={helperText}
            inputSx={{
              '&:hover': {
                backgroundColor: '#f1f5f9',
              },
              '&::after': {
                display: 'none',
              },
            }}
            inputClassName={inputClassName}
            readOnly={isMobileSearch}
            onClick={isMobileSearch ? handleMobileSearch : undefined}
          />
        )}
        options={searchOptions}
        filterOptions={(options) => options}
        onInputChange={(event, value) => {
          setSearchValue(value.trim());
        }}
        getOptionLabel={(option) => option.name}
        isOptionEqualToValue={(option, value) =>
          !!option &&
          value &&
          !!option?.name &&
          !!value?.name &&
          option.name === value.name
        }
        onChange={(e, item) => {
          const selectedOptions = getSelectedOptions(options, item.id);
          onChange(item, selectedOptions);
          setAutoCompleteOpen(false);
          handleSelectOption(DEFAULT_SELECT_OPTION);
        }}
        readOnly={isMobileSearch}
        data-observer-id={dataObserverId}
        data-ele-group={dataEleGroup}
        data-ele-id={dataEleId}
        onFocus={(e) => {
          if (isMobileSearch) return;
          toggleDropdownMenu(true);
          dataEleId && trackClickEvent(e);
          const {
            parent: { ele: parentEle, top: parentTop },
            child: { ele: childEle, top: childTop },
          } = selectOption;
          if (parentEle && childEle) {
            const parentDivEle = parentEle.parentNode as HTMLElement;
            const childDivEle = childEle.parentNode as HTMLElement;
            const childUlEle = parentEle.firstElementChild as HTMLElement;
            if (parentDivEle && childDivEle && childUlEle) {
              parentDivEle.scrollTop = parentTop;
              childDivEle.scrollTop = childTop;
              parentEle.classList.add(SELECT_SELECTED);
              childEle.classList.add(SELECT_SELECTED);
              childUlEle.classList.add(SELECT_SHOW);
            }
          }
        }}
        onInput={() => {
          if (isMobileSearch) return;
          toggleDropdownMenu(false);
          setAutoCompleteOpen(true);
        }}
        onBlur={() => {
          if (isMobileSearch) return;
          toggleDropdownMenu(false);
          setAutoCompleteOpen(false);
        }}
        sx={{ ...sx, ...autocompleteSx }}
        PopperComponent={styledPopper}
        selectOnFocus={false}
        disableClearable={true}
        noOptionsText="抱歉，无此城市的数据"
      />
      <CascadeSelectOptions
        options={options}
        isParentRef={true}
        handleSelect={handleSelect}
        isHalfWidth={isHalfWidth}
        dropdownMenuRef={selectMenuRef}
        className={optionsClassName}
        pageUniqueId={pageUniqueId}
      />
    </div>
  );
};

export default CitySearchOptions;
